import {
  Route,
  Routes,
  Navigate,
  useLocation,
  useParams,
} from "react-router-dom";

import A3Metadata from "@/pages/A3Metadata/main";
import AdminUserActions from "@/pages/AdminUserAction/main";
import F1StrategyMetadata from "@/pages/f1/F1StrategyMetadata/main";
import F1IssuersExclusion from "@/pages/f1/F1IssuersExclusion/main";

import ApiKeys from "@/pages/ApiKey/main";
import AppUsers from "@/pages/AppUser/main";
import Audits from "@/pages/Audit/main";

import B1Metadata from "@/pages/B1Metadata/main";

import ClientPortfolios from "@/pages/ClientPortfolio/main";

import Companies from "@/pages/Company/main";
// import CompanyApprovals from "@/pages/CompanyApproval/main";
import Countries from "@/pages/Country/main";
import DelayedJobs from "@/pages/DelayedJob/main";
import EnumTranslations from "@/pages/EnumTranslation/main";
import EtfStaticData from "@/pages/EtfStaticData/main";
import Events from "@/pages/Event/main";
import Factors from "@/pages/Factor/main";
import FileDeliveries from "@/pages/FileDelivery/main";
import FinancialIndicators from "@/pages/FinancialIndicator/main";
import FoBankContacts from "@/pages/FoBankContact/main";
import ForeignExchangeRates from "@/pages/ForeignExchangeRate/main";
import GroupUsers from "@/pages/GroupUser/main";
import InputFiles from "@/pages/InputFile/main";
import ConnectorConfigs from "@/pages/ConnectorConfig/main";
import Issuers from "@/pages/Issuer/main";
import IssuerEvents from "@/pages/IssuerEvent/main";
import Keywords from "@/pages/Keyword/main";
import LevelPermissions from "@/pages/LevelPermission/main";
import MacroKeywords from "@/pages/MacroKeyword/main";
import OpenFigis from "@/pages/OpenFigi/main";
import Policies from "@/pages/Policy/main";
import PortfolioLinks from "@/pages/PortfolioLink/main";
import ProviderConfigs from "@/pages/ProviderConfig/main";
import RatingConversions from "@/pages/RatingConversion/main";
import S1DownloadCenter from "@/pages/S1DownloadCenter/main";
import S1Metadata from "@/pages/S1Metadata/main";
import S1InvestStrategies from "@/pages/S1InvestStrategies/main";
import S1MonthlyComments from "@/pages/S1MonthlyComments/main";
import S1Positions from "@/pages/S1Positions/main";
import S1DataUpload from "@/pages/S1DataUpload/main";

// import SptRequests from "@/pages/SptRequest/main";
import StockExchanges from "@/pages/StockExchange/main";
import Strategies from "@/pages/Strategy/main";
import StrategyChildren from "@/pages/StrategyChild/main";
import StrategyPermissions from "@/pages/StrategyPermission/main";
import StrategyTimeSeries from "@/pages/StrategyTimeSerie/main";
import StrategyVersions from "@/pages/StrategyVersion/main";
import SessionsActive from "@/pages/SessionsActive/main";
import TimeSerieFiles from "@/pages/TimeSerieFile/main";
import TimeSerieFileDates from "@/pages/TimeSerieFileDate/main";
import TptStaticData from "@/pages/TptStaticData/main";
import Transactions from "@/pages/Transactions/main";
import Urgewalds from "@/pages/Urgewald/main";
import Users from "@/pages/User/main";
import UserPermissions from "@/pages/UserPermission/main";
import Versions from "@/pages/Version/main";
import Views from "@/pages/View/main";
import Workbooks from "@/pages/Workbook/main";

import A5Routes from "@/pages/a5/A5Routes";
import A6Routes from "@/pages/a6/A6Routes";
import E2Routes from "@/pages/e2/E2Routes";
import E3Routes from "@/pages/e3/E3Routes";
import A7FIARoutes from "@/pages/a7/A7FIARoutes.tsx";
import A7OPCVMRoutes from "@/pages/a7/A7OPCVMRoutes";
import A9Routes from "@/pages/a9/A9Routes";
import C3Routes from "@/pages/c3/C3Routes";
import M2Routes from "@/pages/m2/M2Routes";
import C7Routes from "@/pages/c7/C7Routes";
import C5Routes from "@/pages/c5/C5Routes";
import C8Routes from "@/pages/c8/C8Routes";
import G4Routes from "@/pages/g4/G4Routes";
import I1Routes from "@/pages/i1/I1Routes";
import K2Routes from "@/pages/k2/K2Routes";
import K1Routes from "@/pages/k1/K1Routes";
import R1Routes from "@/pages/r1/R1Routes";
import L1Routes from "@/pages/l1/L1Routes";
import { Clients } from "@/lib/clients";
import { ClientTables } from "@/lib/clientTables";
import { Tables } from "@/gen/tableNames/tables";
import N1Routes from "@/pages/n1/N1Routes";
import S3Routes from "@/pages/s3/S3Routes";
import D1Routes from "@/pages/d1/D1Routes";
import S2Routes from "@/pages/s2/S2Routes";

function resolveShortAdminSection(table: string): string | null {
  const t = table.toLowerCase();
  if (t.startsWith("fo_")) return "fo";
  if (t.startsWith("a7_opcvm_")) return "a7-opcvm";
  if (t.startsWith("a7_fia_")) return "a7-fia";
  if (t.startsWith("s1_")) return "s1";

  const m = t.match(/^([a-z]\d+)_/);
  if (m?.[1]) return m[1];

  if (t === "a3_metadata") return "a3";
  if (t === "b1_metadata") return "b1";
  if (t.startsWith("f1_")) return "f1";
  if (t === "s1_metadata") return "s1 - instit";
  return null;
}

function ShortAdminTableRedirect() {
  const location = useLocation();
  const { table, "*": rest } = useParams();
  if (!table) return null;

  const section = resolveShortAdminSection(table);
  if (!section) return null;

  const [adminPrefix] = location.pathname.split("/admin/");
  if (!adminPrefix) return null;

  const targetPath = `${adminPrefix}/admin/${[section, table, rest].filter(Boolean).join("/")}`;
  if (targetPath === location.pathname) return null;

  return (
    <Navigate to={`${targetPath}${location.search}${location.hash}`} replace />
  );
}

export default function Admin() {
  return (
    <Routes>
      {/* Short URLs used by workspace default routing */}
      <Route path="a3_metadata/*" element={<A3Metadata />} />
      <Route path="b1_metadata/*" element={<B1Metadata />} />
      <Route path="s1_metadata/*" element={<S1Metadata />} />
      <Route
        path={`${Tables.S1_MONTHLY_COMMENTS}/*`}
        element={<S1MonthlyComments />}
      />
      <Route
        path={`${Tables.S1_INVEST_STRATEGIES}/*`}
        element={<S1InvestStrategies />}
      />
      <Route path={`${Tables.S1_POSITIONS}/*`} element={<S1Positions />} />
      <Route path={Clients.S1_QUANTILIA}>
        <Route path={`${Tables.S1_MONTHLY_COMMENTS}/*`} element={<S1MonthlyComments />} />
        <Route path={`${Tables.S1_INVEST_STRATEGIES}/*`} element={<S1InvestStrategies />} />
      </Route>
      <Route path=":table/*" element={<ShortAdminTableRedirect />} />

      {/* Feature bundles (unchanged) */}
      <Route path="a5/*" element={<A5Routes />} />
      <Route path="a6/*" element={<A6Routes />} />
      <Route path={`${Clients.D1}/*`} element={<D1Routes />} />
      <Route path={`${Clients.E2}/*`} element={<E2Routes />} />
      <Route path="e3/*" element={<E3Routes />} />
      <Route path="a7-fia/*" element={<A7FIARoutes />} />
      <Route path="a7-opcvm/*" element={<A7OPCVMRoutes />} />
      <Route path="a9/*" element={<A9Routes />} />
      <Route path={`${Clients.S3}/*`} element={<S3Routes />} />
      <Route path={`${Clients.S2}/*`} element={<S2Routes />} />
      <Route path="l1/*" element={<L1Routes />} />
      <Route path="k1/*" element={<K1Routes />} />
      <Route path="r1/*" element={<R1Routes />} />
      <Route path="k2/*" element={<K2Routes />} />
      <Route path={`${Clients.C3}/*`} element={<C3Routes />} />
      <Route path={`${Clients.M2}/*`} element={<M2Routes />} />
      <Route path={`${Clients.C7}/*`} element={<C7Routes />} />
      <Route path="c5/*" element={<C5Routes />} />
      <Route path="c8/*" element={<C8Routes />} />
      <Route path="g4/*" element={<G4Routes />} />
      <Route path="i1/*" element={<I1Routes />} />
      <Route path="n1/*" element={<N1Routes />} />

      {/* ===== Reporting ===== */}
      <Route path="a3">
        <Route path="a3_metadata/*" element={<A3Metadata />} />
      </Route>
      <Route path="a3/*" element={<Navigate to="../a3" replace />} />

      <Route path="b1">
        <Route path="b1_metadata/*" element={<B1Metadata />} />
      </Route>
      <Route path="b1/*" element={<Navigate to="../b1" replace />} />

      <Route path={`${Clients.S1}`}>
        <Route path="s1_download_center/*" element={<S1DownloadCenter />} />
        <Route path="s1_metadata/*" element={<S1Metadata />} />
        <Route path={`${Tables.S1_POSITIONS}/*`} element={<S1Positions />} />
        <Route path={`${ClientTables.S1_DATA_UPLOAD}/*`} element={<S1DataUpload />} />
      </Route>

      <Route path="f1">
        <Route path="f1_strategy_metadata/*" element={<F1StrategyMetadata />} />
        <Route path="f1_issuers_exclusion/*" element={<F1IssuersExclusion />} />
      </Route>
      <Route
        path="f1-reporting/*"
        element={<Navigate to="../f1 reporting" replace />}
      />

      <Route path="s1 - instit">
        <Route path="s1_download_center/*" element={<S1DownloadCenter />} />
        <Route path="s1_metadata/*" element={<S1Metadata />} />
        <Route path={`${Tables.S1_POSITIONS}/*`} element={<S1Positions />} />
      </Route>
      <Route
        path="s1-reporting/*"
        element={<Navigate to="../s1 reporting" replace />}
      />
      {/* ===== QTL - Admin ===== */}
      <Route path="qtl - admin">
        <Route path="api_keys/*" element={<ApiKeys />} />
        <Route path="audits/*" element={<Audits />} />
        <Route path="events/*" element={<Events />} />
      </Route>
      <Route
        path="qtl-admin/*"
        element={<Navigate to="../qtl - admin" replace />}
      />

      {/* ===== QTL - Users ===== */}
      <Route path="qtl - users">
        <Route path="group_users/*" element={<GroupUsers />} />
        <Route path="users/*" element={<Users />} />
        <Route path="sessions_active/*" element={<SessionsActive />} />
        <Route path="sessions-active/*" element={<SessionsActive />} />
      </Route>
      <Route
        path="qtl-users/*"
        element={<Navigate to="../qtl - users" replace />}
      />

      {/* ===== QTL - Dynamic data ===== */}
      <Route path="qtl - dynamic data">
        <Route path="client_portfolios/*" element={<ClientPortfolios />} />
        <Route path="links/*" element={<PortfolioLinks />} />
        <Route path="strategy_time_series/*" element={<StrategyTimeSeries />} />
      </Route>
      <Route
        path="qtl-dynamic-data/*"
        element={<Navigate to="../qtl - dynamic data" replace />}
      />

      {/* ===== QTL - Referential ===== */}
      <Route path="qtl - referential">
        <Route path="companies/*" element={<Companies />} />
        {/* <Route path="company_approvals/*" element={<CompanyApprovals />} /> */}
        <Route path="countries/*" element={<Countries />} />
        <Route path="enum_translations/*" element={<EnumTranslations />} />
        <Route path="etf_static_data/*" element={<EtfStaticData />} />
        <Route
          path="financial_indicators/*"
          element={<FinancialIndicators />}
        />
        <Route
          path="foreign_exchange_rates/*"
          element={<ForeignExchangeRates />}
        />
        <Route path="issuers/*" element={<Issuers />} />
        <Route path="open_figis/*" element={<OpenFigis />} />
        <Route path="rating_conversions/*" element={<RatingConversions />} />
        <Route path="stock_exchanges/*" element={<StockExchanges />} />
        <Route path="strategies/*" element={<Strategies />} />
        <Route path="tpt_static_data/*" element={<TptStaticData />} />
        <Route path="urgewalds/*" element={<Urgewalds />} />
      </Route>
      <Route
        path="qtl-referential/*"
        element={<Navigate to="../qtl - referential" replace />}
      />

      {/* ===== QTL - SPT ===== */}
      <Route path="qtl - spt">
        {/* <Route path="spt_requests/*" element={<SptRequests />} /> */}
        <Route path="strategy_versions/*" element={<StrategyVersions />} />
        <Route path="versions/*" element={<Versions />} />
      </Route>
      <Route
        path="qtl-spt/*"
        element={<Navigate to="../qtl - spt" replace />}
      />

      {/* ===== QTL - ETL ===== */}
      <Route path="qtl - etl">
        <Route path="admin_user_actions/*" element={<AdminUserActions />} />
        <Route path="connector_configs/*" element={<ConnectorConfigs />} />
        <Route path="provider_configs/*" element={<ProviderConfigs />} />
        <Route
          path="time_serie_file_dates/*"
          element={<TimeSerieFileDates />}
        />
        <Route path="time_serie_files/*" element={<TimeSerieFiles />} />
        <Route path="file_deliveries/*" element={<FileDeliveries />} />
        <Route path="input_files/*" element={<InputFiles />} />
      </Route>
      <Route
        path="qtl-etl/*"
        element={<Navigate to="../qtl - etl" replace />}
      />

      {/* ===== QTL - IA ===== */}
      <Route path="qtl - ia">
        <Route path="keywords/*" element={<Keywords />} />
        <Route path="macro_keywords/*" element={<MacroKeywords />} />
      </Route>
      <Route path="qtl-ia/*" element={<Navigate to="../qtl - ia" replace />} />

      {/* ===== QTL - Legacy ===== */}
      <Route path="qtl - legacy">
        <Route path="app_users/*" element={<AppUsers />} />
        <Route path="delayed_jobs/*" element={<DelayedJobs />} />
        <Route path="factors/*" element={<Factors />} />
        <Route path="issuer_events/*" element={<IssuerEvents />} />
        <Route path="level_permissions/*" element={<LevelPermissions />} />
        <Route path="policies/*" element={<Policies />} />
        <Route path="strategy_children/*" element={<StrategyChildren />} />
        <Route
          path="strategy_permissions/*"
          element={<StrategyPermissions />}
        />
        <Route path="transactions/*" element={<Transactions />} />
        <Route path="user_permissions/*" element={<UserPermissions />} />
      </Route>
      <Route
        path="qtl-legacy/*"
        element={<Navigate to="../qtl - legacy" replace />}
      />

      {/* ===== FO ===== */}
      <Route path="fo">
        <Route path="fo_bank_contact/*" element={<FoBankContacts />} />
      </Route>

      {/* ===== QTL - Tableau ===== */}
      <Route path="qtl - tableau">
        <Route path="views/*" element={<Views />} />
        <Route path="workbooks/*" element={<Workbooks />} />
      </Route>
    </Routes>
  );
}
